import { Injectable, Injector } from "@angular/core";
import { collection, DocumentData, Firestore, getDocs, QuerySnapshot } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { CatchErrorService } from "@core/catch-error.service";
import { LoadingService } from "@core/loading.service";
import { catchError, map, of, shareReplay, switchMap, take } from "rxjs";
import { BaseService } from "../app.base.service";
import { CommonService } from "./common/common.service";
import { FirestoreDataService } from "./firestore-data.service";

@Injectable({
  providedIn: "root",
})
export class MarketingCampaignsService extends BaseService {
  invitationData: { psId: ""; member_id: "" };

  constructor(
    private firestore: Firestore,
    private firestoreDataService: FirestoreDataService,
    private catchService: CatchErrorService,
    private loadingService: LoadingService,
    private injector: Injector,
    private commonService: CommonService
  ) {
    super(injector);
  }

  /**
   * This function is used for getting list of all the welcome page data
   * @param actions
   * @returns data
   */
  getWelcomePageData() {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return of(null); // Return null if offline
        }

        const welcomeSliderCollection = collection(this.firestore, "/Global_lookup/welcome/slider"); // Reference collection
        return getDocs(welcomeSliderCollection); // Fetch QuerySnapshot<DocumentData>
      }),
      map((snapshot: QuerySnapshot<DocumentData>) => {
        if (snapshot) {
          return this.firestoreDataService.snapShotData(snapshot); // Process QuerySnapshot<DocumentData>
        }
        return []; // Return empty array if no data
      }),
      shareReplay(1) // Cache results
    );
  }

  // This code is used to open a popup window containing a TypeForm survey.
  // The surveyLink parameter is the URL for the TypeForm survey.
  // The hidden parameter is used to pass the user ID to the TypeForm survey.
  // The onSubmit parameter is used to process the data that is collected by the survey.
  // The onReady parameter is used to dismiss the loading screen when the survey is ready to be shown.
  // The onClose parameter is used to dismiss the loading screen when the survey is closed.
  triggerTypeForm(surveyLink: string) {
    this.commonService.getUid("MarketingCampaignsService").pipe(take(1)).subscribe((userId) => {
      if (!userId) return; // Exit if userId is invalid

      this.loadingService.present();

      window["typeformEmbed"]
        ?.makePopup(surveyLink, {
          hideHeaders: true,
          hideFooter: true,
          hidden: [
            {
              user_id: userId, // ✅ Use userId from commonService
            },
          ],
          onSubmit: function () { },
          onReady: () => {
            this.loadingService.dismiss().catch((err) => {
              console.error("Error dismissing loader:", err);
              return err;
            });
          },
          onClose: function () { },
        })
        ?.open();
    });
  }


  /**
   * The `getMGMDetails` function retrieves data from the AngularFireStore at the path `/MGM/new` and
   * maps the response to extract the data.
   * @returns The `getMGMDetails()` function is returning an Observable that fetches data from the
   * Firestore collection `/MGM/new` using the "get" method. The data is then mapped to extract the
   * `data` field from the response.
   */
  getMGMDetails() {
    return this.firestoreDataService.getAngularFireStore(`/MGM/new`, "get").pipe(
      map((res: Data) => res?.data()),
      catchError(() => of(null))
    );
  }

  // This code adds a user to the database of WordPress users.
  // The name of the function is addWPUser.
  // The parameter is userId.
  // This function is used to add a user to the WordPress database.
  // The userId parameter is used to identify the user that is being added to the database.
  // The user's information is added to the database and the userId is returned.

  addWPUser(userId) {
    return this.wpUserFunction(userId);
  }


  /**
   * Returns the invitation data.
   */
  getInvitationData() {
    return this.invitationData;
  }
}
